import React, { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers';
import { Box, TextField, Popover, IconButton, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const CalendarUI = ({ onDateRangeChange, onSortChange }) => {
    const [startDate, setStartDate] = useState(dayjs().subtract(4, 'week'));
    const [endDate, setEndDate] = useState(dayjs());
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortBy, setSortBy] = useState('newest');

     const { t } = useTranslation();


    // Notify parent on mount with default values
    useEffect(() => {
        if (onDateRangeChange) {
            onDateRangeChange({ 
                from: startDate.toISOString(), 
                to: endDate.toISOString() 
            });
        }
        if (onSortChange) {
            onSortChange('newest');
        }
    }, []); // Run once on mount

    // Notify parent when date range changes
    useEffect(() => {
        if (onDateRangeChange && startDate && endDate) {
            onDateRangeChange({ 
                from: startDate.toISOString(), 
                to: endDate.toISOString() 
            });
        }
    }, [startDate, endDate, onDateRangeChange]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSortChange = (event) => {
        const newSortValue = event.target.value;
        setSortBy(newSortValue);
        
        // Notify parent component
        if (onSortChange) {
            onSortChange(newSortValue);
        }
    };

    const open = Boolean(anchorEl);

    const formatDateRange = () => {
        if (startDate && endDate) {
            return `${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(endDate).format('MMM DD, YYYY')}`;
        } else if (startDate) {
            return `${dayjs(startDate).format('MMM DD, YYYY')} - Select end date`;
        }
        return 'Select date range';
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{  display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    value={formatDateRange()}
                    onClick={handleClick}
                    placeholder="Select date range"
                    label={t('translation:pages.dlm.calender_filters.dateRangeLabel')}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <IconButton onClick={handleClick}>
                                <CalendarToday />
                            </IconButton>
                        ),
                    }}
                    sx={{ cursor: 'pointer', minWidth: '300px' }}
                />
                
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="sort-by-label">{t('translation:pages.dlm.calender_filters.sortByLabel')}</InputLabel>
                    <Select
                        labelId="sort-by-label"
                        id="sort-by-select"
                        value={sortBy}
                        label={`${t('translation:pages.dlm.calender_filters.sortBy')}`}
                        onChange={handleSortChange}
                    >
                        <MenuItem value="createdBy">{t('translation:pages.dlm.calender_filters.createdBy')}</MenuItem>
                        <MenuItem value="newest">{t('translation:pages.dlm.calender_filters.newest')}</MenuItem>
                        <MenuItem value="oldest">{t('translation:pages.dlm.calender_filters.oldest')}</MenuItem>
                    </Select>
                </FormControl>

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                                From
                            </Typography>
                            <DateCalendar
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                            />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                                To
                            </Typography>
                            <DateCalendar
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                minDate={startDate}
                            />
                        </Box>
                    </Box>
                </Popover>
            </Box>
        </LocalizationProvider>
    );
};

export default CalendarUI;